// Skills v2 library — Gallery tab (2026-08-19). Mockup #s5 top half.
//
// Browse + rate only. Adding a skill to an agent stays the on-agent flow
// (Agent → Overview → Skills). No fake "Install" button — the detail drawer
// tells the user where to add it.

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi } from "../../api/client";
import {
  formatLift, dataFitLabel, stars, installLabel, deriveCategories,
} from "./skillLibFormat";
import SkillDetailDrawer from "./SkillDetailDrawer";
import AttachToAgentModal from "./AttachToAgentModal";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

function GalleryCard({ skill, onOpen, onAdd }) {
  const lift = formatLift(skill.eval_lift);
  const fit = dataFitLabel(skill.data_fit);
  const star = stars(skill.avg_rating);
  const installs = installLabel(skill.install_count);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(skill)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(skill); } }}
      style={{
        textAlign: "left", cursor: "pointer",
        border: `1px solid ${BORDER}`, borderRadius: 16, background: COLORS.surface,
        boxShadow: MATERIALS.spatialShadow(), padding: "14px 15px",
        display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>{skill.icon || "✦"}</span>
        <h3 style={{ fontSize: 13.5, margin: 0, color: COLORS.text }}>{skill.display_name || skill.name}</h3>
      </div>
      <div style={{ color: MUTED, fontSize: 12, lineHeight: 1.4, minHeight: 34 }}>{skill.description}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: "auto" }}>
        {lift && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700,
            padding: "3px 9px", borderRadius: 999, color: GREEN,
            background: `${GREEN}22`, border: `1px solid ${GREEN}66`,
          }}>{lift}</span>
        )}
        {fit && (
          <span style={{ fontSize: 11.5, color: skill.data_fit === "works_with_data" ? GREEN : DIM }}>{fit}</span>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {star && <span style={{ color: COLORS.orange || "#f59e0b", fontSize: 12 }}>{star}</span>}
        {installs && <span style={{ fontSize: 11, color: DIM }}>{installs}</span>}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdd(skill); }}
          style={{
            font: "inherit", fontSize: 11.5, fontWeight: 700, color: "#fff", background: COLORS.accent,
            border: "none", borderRadius: 999, padding: "5px 12px", cursor: "pointer",
          }}
        >＋ Add to an agent</button>
      </div>
    </div>
  );
}

export default function SkillGalleryTab({ onAuthor }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("All");
  const [openSkill, setOpenSkill] = useState(null);
  const [attachSkill, setAttachSkill] = useState(null);

  const goAdopt = (agentId) => {
    setAttachSkill(null);
    setOpenSkill(null);
    navigate(`/library/${agentId}`);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = unwrapArray(await skillsApi.gallery());
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setError("Couldn't load the skills gallery.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => deriveCategories(rows), [rows]);
  const visible = useMemo(
    () => (category === "All" ? rows : rows.filter((r) => r.category === category)),
    [rows, category]
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>Skills gallery</h2>
        <p style={{ margin: "4px 0 0", color: DIM, fontSize: 13, maxWidth: "66ch" }}>
          A shelf of ready-made superpowers for your agents. Every skill shows a real,
          eval-earned lift and whether it fits your data — so you can trust the shelf.
        </p>
      </div>

      {/* Category chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {categories.map((c) => {
          const on = c === category;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              style={{
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                padding: "6px 12px", borderRadius: 999,
                color: on ? "#fff" : MUTED,
                background: on ? COLORS.accent : COLORS.surface,
                border: `1px solid ${on ? COLORS.accent : BORDER}`,
              }}
            >{c}</button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: DIM }}>Loading the gallery…</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: COLORS.red || "#ef4444" }}>{error}</div>
      ) : (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(258px, 1fr))", gap: 11,
          }}>
            {visible.map((skill) => (
              <GalleryCard key={skill.name} skill={skill} onOpen={setOpenSkill} onAdd={setAttachSkill} />
            ))}
            {/* Author your own */}
            <button
              type="button"
              onClick={() => onAuthor && onAuthor()}
              style={{
                cursor: "pointer", border: `1px dashed ${COLORS.accent}`, borderRadius: 16,
                background: COLORS.accentSoft || "transparent", padding: "14px 15px",
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", gap: 6, color: DIM, minHeight: 120,
              }}
            >
              <div style={{ fontSize: 24 }}>✦</div>
              <h3 style={{ color: COLORS.text, fontSize: 13.5, margin: 0 }}>Author your own</h3>
              <div style={{ fontSize: 12 }}>Describe it in a sentence.</div>
            </button>
          </div>

          {visible.length === 0 && (
            <div style={{ fontSize: 12.5, color: DIM, marginTop: 12 }}>
              No skills in this category yet.
            </div>
          )}

          <div style={{
            display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, padding: "11px 14px",
            borderRadius: 12, background: COLORS.accentSoft || "transparent",
            border: `1px solid ${COLORS.accent}`, fontSize: 12.5, color: MUTED,
          }}>
            <span>✅</span>
            <div>
              <b style={{ color: COLORS.text }}>Trustworthy by construction.</b> A skill only shows a
              lift badge if it earned it in an eval — the shelf isn't a junk drawer.
            </div>
          </div>
        </>
      )}

      {openSkill && (
        <SkillDetailDrawer
          skill={openSkill}
          onClose={() => setOpenSkill(null)}
          onAdd={(s) => setAttachSkill(s || openSkill)}
          onEdit={(s) => { setOpenSkill(null); navigate(`/skills?tab=author&edit=${encodeURIComponent(s.name)}`); }}
        />
      )}
      {attachSkill && (
        <AttachToAgentModal
          skillName={attachSkill.name}
          displayName={attachSkill.display_name || attachSkill.name}
          onClose={() => setAttachSkill(null)}
          onAttached={goAdopt}
        />
      )}
    </div>
  );
}
